import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const FundMappingPage = () => {
  const [items, setItems] = useState([]);
  const [funds, setFunds] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ fund_id: '', fund_name: '', gl_account_code: '', gl_account_name: '', mapping_type: 'asset', notes: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [mappingsRes, fundsRes, coaRes] = await Promise.all([
        api.get('/fund-mappings'),
        api.get('/funds'),
        api.get('/chart-of-accounts')
      ]);
      setItems(mappingsRes.data);
      setFunds(fundsRes.data);
      setAccounts(coaRes.data);
    } catch { setItems([]); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.fund_name || !form.gl_account_code) { toast.error('Fund and GL Account are required'); return; }
    try { await api.post('/fund-mappings', form); toast.success('Fund mapping created'); setShowModal(false); setForm({ fund_id: '', fund_name: '', gl_account_code: '', gl_account_name: '', mapping_type: 'asset', notes: '' }); load(); }
    catch { toast.error('Failed to create mapping'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    try { await api.delete(`/fund-mappings/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const selectFund = (fundId) => {
    const fund = funds.find(f => f.id === fundId);
    setForm({ ...form, fund_id: fundId, fund_name: fund?.name || '' });
  };

  const selectAccount = (code) => {
    const acc = accounts.find(a => a.account_code === code);
    setForm({ ...form, gl_account_code: code, gl_account_name: acc?.account_name || '' });
  };

  const typeColor = (t) => {
    const map = { asset: 'text-blue-700 bg-blue-50', liability: 'text-red-700 bg-red-50', equity: 'text-purple-700 bg-purple-50', income: 'text-emerald-700 bg-emerald-50', expense: 'text-amber-700 bg-amber-50' };
    return map[t] || 'text-gray-700 bg-gray-50';
  };

  return (
    <AdminSubPage title="Fund Mapping" breadcrumb={['Admin', 'Organization', 'Fund Mapping']} backPath="/dashboard/admin/organization" actionLabel="+ Create Mapping" onAction={() => setShowModal(true)}>
      <div className="bg-white border border-gray-200" data-testid="fund-mapping-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Fund</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">GL Account</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Notes</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-gray-400"><Link2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />No fund mappings defined.</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`mapping-row-${item.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{item.fund_name}</div><div className="text-xs text-gray-400 font-mono">{item.fund_id}</div></td>
                <td className="px-4 py-3"><div className="text-sm text-[#1f1f1f]">{item.gl_account_name}</div><div className="text-xs text-gray-400 font-mono">{item.gl_account_code}</div></td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs font-medium capitalize ${typeColor(item.mapping_type)}`}>{item.mapping_type}</span></td>
                <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">{item.notes || '-'}</td>
                <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent aria-describedby="fm-desc"><DialogHeader><DialogTitle>Create Fund Mapping</DialogTitle></DialogHeader>
          <p id="fm-desc" className="sr-only">Map a fund to a GL account</p>
          <div className="space-y-4">
            <div><Label>Fund *</Label>
              {funds.length > 0 ? (
                <Select value={form.fund_id} onValueChange={selectFund}>
                  <SelectTrigger data-testid="fm-fund-select"><SelectValue placeholder="Select fund" /></SelectTrigger>
                  <SelectContent>{funds.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Input placeholder="Fund name" value={form.fund_name} onChange={e => setForm({...form, fund_name: e.target.value})} />}
            </div>
            <div><Label>GL Account *</Label>
              {accounts.length > 0 ? (
                <Select value={form.gl_account_code} onValueChange={selectAccount}>
                  <SelectTrigger data-testid="fm-account-select"><SelectValue placeholder="Select GL account" /></SelectTrigger>
                  <SelectContent>{accounts.map(a => <SelectItem key={a.account_code} value={a.account_code}>{a.account_code} - {a.account_name}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Input placeholder="Account code" value={form.gl_account_code} onChange={e => setForm({...form, gl_account_code: e.target.value})} />}
            </div>
            <div><Label>Mapping Type</Label>
              <Select value={form.mapping_type} onValueChange={v => setForm({...form, mapping_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="asset">Asset</SelectItem>
                  <SelectItem value="liability">Liability</SelectItem>
                  <SelectItem value="equity">Equity</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="fm-submit-btn">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default FundMappingPage;
